<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <RequestLifeCycle
            ref="requestLifeCycle"
            :life-cycle="request.lifeCycle"
        />
        <b-card ref="verticalStepComponent" id="simple-flow">
            <b-row class="mb-1">
                <b-col md="4" class="d-flex align-items-center">
                    <span class="align-middle">
                        <StatusBadge
                            type="received"
                            :status="request.status"
                        />
                    </span>
                </b-col>
                <b-col md="8">
                    <Actions
                        :request="request"
                        :show-save="isSaveButtonVisible"
                        :is-reply-disabled="isReplyDisabled"
                        :show-reply="isReplyButtonVisible"
                        :show-forward="false"
                        :is-life-cycle-visible="request.lifeCycle.length > 0"
                        @showLifeCycle="showLifeCycle"
                        @close="close"
                        class="float-right"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <h2 class="text-center">
                        {{ $t('Page(ReceivedRequest):Component(Text):page-label', {customer: request.customer.title, product: getProductTitle()}) }}
                    </h2>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <ViewRequest
                        :request="request"
                    />
                </b-col>
            </b-row>

            <h4 class="reply-process-heading">{{ $t('Page(ReceivedRequest):Component(Text):reply-process') }}</h4>

            <app-collapse
                type="margin"
                class="vertical-steps-flow"
            >
                <app-collapse-item
                    :title="getStepTitle(stepIds.generalProductInformation)"
                    :header-bg-variant="firstStepStyle.bg"
                    :header-text-variant="firstStepStyle.text"
                    :class="firstStepStyle.class"
                    is-visible
                    ref="stepGeneralProductInformation"
                >
                    <Overlay :show="generalProductInformationLoading">
                        <LegacyBulkProductInformation
                            v-if="isExternalBulkReplyMode"
                            class="mt-1"
                            :request-id="id"
                            :product-id="productId"
                            :request="request"
                            :product="product"
                            :candidates="bulkReplyCandidates"
                            :selected-request-ids="selectedBulkReplyRequestIds"
                            :loading="bulkReplyCandidatesLoading"
                            :busy="bulkReplySelectionUpdating"
                            :error="bulkReplyCandidatesError"
                            @remove="removeBulkReplyRequest"
                            @retry="loadBulkReplyCandidates({ force: true })"
                        />
                        <ProductMaterialMixtureCategorySelector
                            v-if="isExternalBulkReplyMode && isMaterialMixtureCategoryLegislationRequested"
                            class="mt-1"
                            :material-category-ids="product.materialCategoryIds || []"
                            :mixture-category-ids="product.mixtureCategoryIds || []"
                            @change="onBulkMaterialMixtureCategoryChange"
                        />
                        <ProductInformation
                            v-else
                            ref="ProductInformation"
                            class="mt-1"
                            :product="product"
                            :request="request"
                            :customer-product="request.customerProduct"
                            :included-substances="includedSubstances"
                            :show-material-mixture-category-selector="isMaterialMixtureCategoryLegislationRequested"
                            @validityChanged="(isValid) => changeStepValidity(stepIds.generalProductInformation, isValid)"
                            @change="saveGeneralProductInformation"
                        />
                    </Overlay>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(stepIds.generalProductInformation)"
                            @click="() => doneStep(stepIds.generalProductInformation)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible(stepIds.concernElements)"
                    :title="getStepTitle(stepIds.concernElements)"
                    :class="getStepVariantStyles(stepIds.concernElements).class"
                    :header-bg-variant="getStepVariantStyles(stepIds.concernElements).bg"
                    :header-text-variant="getStepVariantStyles(stepIds.concernElements).text"
                    ref="stepConcernElements"
                >
                    <ConcernElements
                        class="simple-flow-step-concern-elements"
                        v-if="renderStage > 1"
                        ref="ConcernElementsComponent"
                        :request="request"
                        :product="product"
                        :compact-view="concernElementsCompactView"
                        :included-substances="includedSubstances"
                        :excluded-substances="excludedSubstances"
                        :loading="saveSubstanceLoading || saveListQuestionStateLoading"
                        :show-questions="true"
                        @validityChanged="(isValid) => changeStepValidity(stepIds.concernElements, isValid)"
                        @undefined="markSubstanceUndefined"
                        @include="markSubstanceIncluded"
                        @exclude="markSubstanceExcluded"
                        @listQuestionStateUpdated="listQuestionStateUpdated"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(stepIds.concernElements)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible(stepIds.furtherProductInformation)"
                    :title="getStepTitle(stepIds.furtherProductInformation)"
                    :class="getStepVariantStyles(stepIds.furtherProductInformation).class"
                    :header-bg-variant="getStepVariantStyles(stepIds.furtherProductInformation).bg"
                    :header-text-variant="getStepVariantStyles(stepIds.furtherProductInformation).text"
                    :disabled="isStepDisabled(stepIds.furtherProductInformation)"
                    ref="stepFurtherProductInformation"
                >
                    <FurtherProductInformation
                        :groups="legislationQuestionnaire"
                        :legislations="regulationQuestionLegislations"
                        :answers="legislationQuestionAnswers"
                        :loading="legislationQuestionnaireOverlayLoading || furtherProductInformationLoading"
                        @answersChange="onFurtherProductInformationAnswersChange"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(stepIds.furtherProductInformation)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible(stepIds.verification)"
                    :title="getStepTitle(stepIds.verification)"
                    :class="getStepVariantStyles(stepIds.verification).class"
                    :header-bg-variant="getStepVariantStyles(stepIds.verification).bg"
                    :header-text-variant="getStepVariantStyles(stepIds.verification).text"
                    ref="stepVerification"
                >
                    <InstructionText class="simple-flow-instruction">
                        {{ $t('Page(ReceivedRequestSimpleFlow):Component(Instruction):verification-documents') }}
                    </InstructionText>
                    <Overlay :show="showDocumentLoading">
                        <Verification
                            class="simple-flow-step-verification"
                            ref="verification"
                            :product="product"
                            :id="productId"
                            :visible-elements="verificationDocuments"
                            :compact-view="verificationCompactView"
                            :evidence="evidenceReports"
                            :compliant-statement="compliantStatement"
                            :compliant-statement-border-variant="compliantStatementStyle.border"
                            :evidence-report-border-variant="evidenceReportStyle.border"
                            :manage-predefined="false"
                            :requested-legislations="request.legislations"
                            :default-compliant-statement-legislation-list="requestedLegislationIds"
                            :default-evidence-report-legislation-list="requestedLegislationIds"
                            @addCompliantStatement="addCompliantStatement"
                            @removeCompliantStatement="removeCompliantStatement"
                            @compliantStatementChange="documentUpdated"
                            @evidenceReportChange="documentUpdated"
                            @addReport="addReport"
                            @removeReport="removeEvidenceReport"
                        />
                    </Overlay>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(stepIds.verification)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible(stepIds.otherInformation)"
                    :title="getStepTitle(stepIds.otherInformation)"
                    :class="getStepVariantStyles(stepIds.otherInformation).class"
                    :header-bg-variant="getStepVariantStyles(stepIds.otherInformation).bg"
                    :header-text-variant="getStepVariantStyles(stepIds.otherInformation).text"
                    ref="stepOtherInformation"
                >
                    <Overlay :show="otherReplyInformationLoading">
                        <InstructionText class="simple-flow-instruction">
                            <span v-html="$t('Page(ReceivedRequestSimpleFlow):Component(Instruction):other-information')"/>
                        </InstructionText>

                        <OtherReplyInformation
                            class="simple-flow-step-other-information"
                            :request="request"
                            @validityChange="(isValid) => changeStepValidity(stepIds.otherInformation, isValid)"
                            @input="onOtherInformationChange"
                        />

                        <div class="text-center">
                            <DoneGoToNextStep
                                class="mt-1"
                                @click="completeOtherInformation"
                            />
                        </div>
                    </Overlay>
                </app-collapse-item>
                <app-collapse-item
                    :title="getStepTitle(stepIds.approveDeclaration)"
                    v-if="isStepVisible(stepIds.approveDeclaration) && isApproveDeclarationStepVisible"
                    :disabled="isApproveDeclarationStepDisabled"
                    :class="getStepVariantStyles(stepIds.approveDeclaration).class"
                    :header-bg-variant="getStepVariantStyles(stepIds.approveDeclaration).bg"
                    :header-text-variant="getStepVariantStyles(stepIds.approveDeclaration).text"
                    ref="stepApproveDeclaration"
                >
                    <InstructionText class="simple-flow-instruction">
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):approve-declaration')"/>
                    </InstructionText>
                    <DeclarationApproval
                        class="no-border simple-flow-step-approve-declaration"
                        label=""
                        :request-id="id"
                        @confirmed="declarationApproved"
                    />
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible(stepIds.approveDeclaration) && isBulkReplyCandidatesStepVisible"
                    :disabled="!isReplyStepActive"
                    :title="getStepTitle(stepIds.similarReplyRequestSelection)"
                    ref="stepBulkReplyCandidates"
                >
                    <SimilarReplyRequestSelection
                        :requests="bulkReplyCandidates"
                        :selected-request-ids="selectedBulkReplyRequestIds"
                        :error="bulkReplyCandidatesError"
                        @update:selected-request-ids="updateSelectedBulkReplyRequestIds"
                        @retry="retryBulkReplyCandidatesAfterOtherInformation"
                    />
                    <div v-if="hasBulkReplyCandidates" class="text-center">
                        <DoneGoToNextStep class="mt-1" @click="() => doneStep(stepIds.similarReplyRequestSelection)" />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isFinalStepVisible"
                    :disabled="!isReplyStepActive"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-final')"
                    ref="stepSendReply"
                >
                    <SendReply
                        :reply-details="request.replyDetails"
                        @send="sendReply"
                    />
                </app-collapse-item>
            </app-collapse>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BRow, BCol, BOverlay} from 'bootstrap-vue'
import {Actions} from "@/views/request/received/components";
import StatusBadge from "@/views/request/components/StatusBadge";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {ProductCardMixins} from "@/views/product/components/product-card";
import {ProductType, VerificationDocumentType} from "@/libs/product";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import {
    ProductInformation, ConcernElements, OtherReplyInformation, FurtherProductInformation,
} from "@/views/request/received/flow/components/simple";
import {
    ViewRequest, ScipInformation,
    ProductComponentsDefinition, ManufacturingOriginDeclaration
} from "@/views/request/received/flow/components/vertiacal-steps";
import {Verification} from "@/views/product/components/product-card";
import SendReply from "@/views/request/received/flow/components/vertiacal-steps/SendReply";
import verticalStepsMixin from "@/views/request/received/flow/vertical-steps-mixin";
import stepFlowMixin from "@/views/request/received/flow/mixins/step-flow-mixin";
import LegislationQuestionnaireMixin from '@/views/product/components/product-card/LegislationQuestionnaireMixin'
import DoneGoToNextStep from "@/views/request/received/flow/components/vertiacal-steps/DoneGoToNextStep";
import CustomCodes from "@/views/request/received/flow/components/vertiacal-steps/CustomCodes";
import {ProductProvider} from "@core/domain/product";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import Overlay from "@core/components/common/Overlay.vue";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";
import SimilarReplyRequestSelection from "@/views/request/received/components/send-reply/SimilarReplyRequestSelection.vue";
import bulkReplyCandidatesMixin from "@/views/request/received/flow/mixins/bulk-reply-candidates-mixin";
import bulkReplyMixin from "@/views/request/received/flow/mixins/bulk-reply-mixin";
import LegacyBulkProductInformation from "@/views/request/received/flow/components/bulk-reply/LegacyBulkProductInformation.vue";
import MaterialMixtureCategoryLegislationMixin from "@/views/product/components/product-card/common/categories/material-mixture-category-legislation-mixin";
import ProductMaterialMixtureCategorySelector from "@/views/product/components/product-card/common/categories";

const StepId = {
    GENERAL_PRODUCT_INFORMATION: 'general-product-information',
    CONCERN_ELEMENTS: 'concern-elements',
    FURTHER_PRODUCT_INFORMATION: 'further-product-information',
    VERIFICATION: 'verification',
    OTHER_INFORMATION: 'other-information',
    SIMILAR_REPLY_REQUEST_SELECTION: 'similar-reply-request-selection',
    APPROVE_DECLARATION: 'approve-declaration',
}

const cellcombFlowSteps = [
    {
        id: StepId.GENERAL_PRODUCT_INFORMATION,
        key: 'generalProductInformation',
        persistedStep: 1,
        ref: 'stepGeneralProductInformation',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):general-product-information-label',
        title: 'General product information',
        validityMode: 'manual',
    },
    {
        id: StepId.CONCERN_ELEMENTS,
        key: 'concernElements',
        persistedStep: 2,
        ref: 'stepConcernElements',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):concern-elements-label',
        title: 'Concern elements',
        validityMode: 'done-click',
    },
    {
        id: StepId.FURTHER_PRODUCT_INFORMATION,
        key: 'furtherProductInformation',
        persistedStep: 3,
        ref: 'stepFurtherProductInformation',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):further-product-information-label',
        title: 'Further product information',
        isDisabled: 'isFurtherProductInformationStepDisabled',
        validityMode: 'done-click',
    },
    {
        id: StepId.VERIFICATION,
        key: 'verification',
        persistedStep: 4,
        ref: 'stepVerification',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):verification-documents-label',
        title: 'Verification documents',
        validityMode: 'done-click',
    },
    {
        id: StepId.OTHER_INFORMATION,
        key: 'otherInformation',
        persistedStep: 5,
        ref: 'stepOtherInformation',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):other-information-label',
        title: 'Other information',
        validityMode: 'done-click',
    },
    {
        id: StepId.APPROVE_DECLARATION,
        key: 'approveDeclaration',
        persistedStep: 6,
        ref: 'stepApproveDeclaration',
        titleKey: 'Page(ReceivedRequest):Component(StepItem):approve-declaration-label',
        title: 'Approve declaration',
        approvalStep: true,
        replyFallback: true,
        validityMode: 'manual',
    },
    {
        id: StepId.SIMILAR_REPLY_REQUEST_SELECTION,
        key: 'similarReplyRequestSelection',
        ref: 'stepBulkReplyCandidates',
        titleKey: 'Page(ReceivedRequest):Component(SimilarReplyRequestSelection):step-label',
        title: 'Reply to similar requests',
        isVisible: 'isBulkReplyCandidatesStepVisible',
        numbered: false,
        persistProgress: false,
        validityMode: 'done-click',
    },
]

export default {
    mixins: [verticalStepsMixin, stepFlowMixin, LegislationQuestionnaireMixin, MaterialMixtureCategoryLegislationMixin, bulkReplyCandidatesMixin, bulkReplyMixin],
    components: {
        RequestLifeCycle,
        Overlay,
        InstructionText,
        BCard, BRow, BCol, Actions, ViewRequest, ProductInformation, ScipInformation, Verification,
        BOverlay, AppCollapse, AppCollapseItem, StatusBadge, ConcernElements,
        OtherReplyInformation, DeclarationApproval, SendReply, ProductComponentsDefinition, DoneGoToNextStep,
        CustomCodes, ManufacturingOriginDeclaration, FurtherProductInformation, SimilarReplyRequestSelection,
        LegacyBulkProductInformation, ProductMaterialMixtureCategorySelector,
    },
    props: {
        id: {
            required: true,
        }
    },
    data() {
        return {
            renderStage: 1,
            verificationDocuments: ['compliant_statement', 'evidence'],
            flowSteps: cellcombFlowSteps,

            otherInformation: null,
            isReplyDisabled: true,
            isReplyButtonVisible: false,
            isSaveButtonVisible: false,
            productId: null,

            saveDocumentTimeout: null,
            showDocumentLoading: false,

            saveCardTimeouts: {},
            generalProductInformationLoading: false,
            furtherProductInformationLoading: false,

            saveSubstanceTimeout: null,
            saveSubstanceLoading: false,
            saveListQuestionStateTimeout: null,
            saveListQuestionStateLoading: false,

            verificationCompactView: false,
            concernElementsCompactView: false,

            requestedLegislationIds: [],

            resizeObserver: null,

            ...ProductCardMixins.data,
        };
    },
    methods: {
        markSubstanceUndefined(s) {
            this.makeSubstanceUndefined(s);
            this.saveSubstances();
        },
        markSubstanceIncluded(s) {
            this.includeConcernSubstance(s);
            this.saveSubstances();
        },
        markSubstanceExcluded(s) {
            this.excludeConcernSubstance(s);
            this.saveSubstances();
        },

        saveSubstances() {
            clearTimeout(this.saveSubstanceTimeout);
            this.saveSubstanceTimeout = setTimeout(() => {
                this.saveSubstanceLoading = true
                ProductProvider(this.getProductId())
                    .saveSubstances()
                    .finally(() => this.saveSubstanceLoading = false);
            }, 2000);
        },
        saveGeneralProductInformation() {
            this.saveCardForStep(
                StepId.GENERAL_PRODUCT_INFORMATION,
                'generalProductInformationLoading'
            );
        },
        onBulkMaterialMixtureCategoryChange({ materialCategoryIds, mixtureCategoryIds }) {
            this.$set(this.product, 'materialCategoryIds', materialCategoryIds);
            this.$set(this.product, 'mixtureCategoryIds', mixtureCategoryIds);
            this.saveGeneralProductInformation();
        },
        saveFurtherProductInformation() {
            this.saveCardForStep(
                StepId.FURTHER_PRODUCT_INFORMATION,
                'furtherProductInformationLoading'
            );
        },
        async completeOtherInformation() {
            await this.loadBulkReplyCandidates();

            if (this.isBulkReplyCandidatesStepVisible && this.isApproveDeclarationStepDisabled) {
                this.changeStepValidity(StepId.OTHER_INFORMATION, true)
                this.markStepAsDone(StepId.OTHER_INFORMATION)
                this.$nextTick(() => {
                    this.$refs.stepBulkReplyCandidates.updateVisible(true)
                    setTimeout(() => this.smoothScroll(this.stepIds.similarReplyRequestSelection), 200)
                })
                return
            }

            this.doneStep(StepId.OTHER_INFORMATION);
        },
        declarationApproved() {
            const approvalStepId = this.stepIds.approveDeclaration

            this.isDeclarationApproved = true
            this.changeStepValidity(approvalStepId, true)
            this.$store.dispatch('declarationApproved')

            this.$nextTick(() => {
                if (this.isBulkReplyCandidatesStepVisible && this.$refs.stepBulkReplyCandidates) {
                    this.$refs.stepBulkReplyCandidates.updateVisible(true)
                    setTimeout(() => this.smoothScroll(this.stepIds.similarReplyRequestSelection), 200)
                } else if (this.$refs.stepSendReply) {
                    this.$refs.stepSendReply.updateVisible(true)
                }
            })
        },
        async retryBulkReplyCandidatesAfterOtherInformation() {
            await this.loadBulkReplyCandidates({ force: true });

            if (this.bulkReplyCandidatesError) {
                return;
            }

            if (this.isBulkReplyCandidatesStepVisible) {
                this.$nextTick(() => {
                    this.$refs.stepBulkReplyCandidates.updateVisible()
                    setTimeout(() => this.smoothScroll(this.stepIds.similarReplyRequestSelection), 200)
                });
                return;
            }

            if (this.$refs.stepSendReply) {
                this.$refs.stepSendReply.updateVisible(true)
            }
        },
        saveCardForStep(step, loadingProperty, timeout = 2500) {
            clearTimeout(this.saveCardTimeouts[step]);
            this.saveCardTimeouts[step] = setTimeout(() => {
                this[loadingProperty] = true;
                this.saveProductCard()
                    .finally(() => {
                        this[loadingProperty] = false;
                    });
            }, timeout);
        },

        documentUpdated(document) {
            if (document.id || (!document.id && document.document)) {
                clearTimeout(this.saveDocumentTimeout);
                this.saveDocumentTimeout = setTimeout(() => {
                    this.showDocumentLoading = true;
                    ProductProvider(this.getProductId())
                        .saveVerificationDocuments()
                        .then(() => {
                            this.fetchDocuments();
                        })
                        .finally(() => this.showDocumentLoading = false);
                }, 2500);
            }
        },

        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },

        removeEvidenceReport(document, index) {
            this.evidenceReports.splice(index, 1);
        },

        close() {
            this.$router.push({
                name: 'received-requests',
            });
        },

        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },

        ...ProductCardMixins.methods,
        listQuestionStateUpdated(list, state) {
            ProductCardMixins.methods.listQuestionStateUpdated.call(this, list, state);
            this.scheduleListQuestionStatesSave();
        },
        scheduleListQuestionStatesSave() {
            clearTimeout(this.saveListQuestionStateTimeout);
            this.saveListQuestionStateTimeout = setTimeout(() => {
                this.saveListQuestionStateLoading = true;
                this.saveProductCard()
                    .finally(() => {
                        this.saveListQuestionStateLoading = false;
                    });
            }, 1500);
        },

        getProductId() {
            return this.request ? this.request.product.id : null;
        },
        getLegislationQuestionnaireLegislations() {
            return this.request && Array.isArray(this.request.legislations)
                ? this.request.legislations
                : [];
        },
        getLegislationQuestionnaireScope() {
            return this.request ? this.request.questionnaireVisibilityScope || null : null;
        },
        getLegislationQuestionAnswersProductId() {
            return this.getProductId();
        },
        onFurtherProductInformationAnswersChange(answers) {
            this.onLegislationQuestionAnswersChange(answers);
            this.saveFurtherProductInformation();
        },

        onResize() {
            this.verificationCompactView = this.$refs.verticalStepComponent.offsetWidth < 1500;
            this.concernElementsCompactView = this.$refs.verticalStepComponent.offsetWidth < 1145;
        },

        managePredefinedDocuments() {
            //  HAM-510
            const verification = this.$refs.verification;

            if (!verification) {
                return;
            }

            //  Reports
            if (this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                if (this.request.evidenceReportTypes.length > 0) {
                    const requestedTypes = this.request.evidenceReportTypes.reduce((requestedTypes, type) => {
                        if (!requestedTypes.hasOwnProperty(type.id)) {
                            requestedTypes[type.id] = type;
                        }
                        return requestedTypes;
                    }, {});
                    this.evidenceReports
                        .forEach((document) => {
                            document.documentTypes.map(type => {
                                if (requestedTypes.hasOwnProperty(type.id)) {
                                    delete requestedTypes[type.id];
                                }
                            });
                        })
                    ;

                    Object.keys(requestedTypes).forEach((key) => {
                        const report = verification.makeEvidenceReport();
                        report.documentTypes.push(requestedTypes[key]);
                        this.addReport(report);
                    });
                } else if (this.evidenceReports.length === 0) {
                    this.addReport(verification.makeEvidenceReport());
                }
            }

            if (
                this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)
                && this.compliantStatement.length === 0
            ) {
                this.addCompliantStatement(verification.makeCompliantStatement());
            }
        },
        fetched() {
            this.renderStage = 2;
            this.requestedLegislationIds = this.request.legislations.map(l => l.id);
            this.fetchLegislationQuestionAnswers();
            this.$nextTick(() => this.managePredefinedDocuments());
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs.verticalStepComponent);
    },
    computed: {
        isFinalStepVisible() {
            //  if last declarative step visible then it should be visible
            if (!this.isApproveDeclarationStepVisible) {
                return this.isStepVisible(StepId.OTHER_INFORMATION);
            }
            return this.isStepVisible(StepId.APPROVE_DECLARATION);
        },
        isVerificationStepRendered() {
            return this.isStepVisible(StepId.VERIFICATION);
        },
        firstStepStyle() {
            return this.getStepVariantStyles(StepId.GENERAL_PRODUCT_INFORMATION);
        },
        isMaterialMixtureCategoryLegislationRequested() {
            return this.hasMaterialMixtureCategoryLegislation(this.request.legislations);
        },
        isBulkGeneralProductInformationValid() {
            if (!this.isExternalBulkReplyMode) {
                return false;
            }

            const categoriesValid = !this.isMaterialMixtureCategoryLegislationRequested
                || (this.product.materialCategoryIds || []).length > 0
                || (this.product.mixtureCategoryIds || []).length > 0;

            return this.isExternalBulkReplySelectionValid && categoriesValid;
        },
        isFurtherProductInformationStepDisabled() {
            return this.legislationQuestionnaireOverlayLoading || !this.isRegulationQuestionsTabVisible;
        },
        regulationQuestionLegislations() {
            return Array.isArray(this.request.legislations)
                ? this.request.legislations
                : [];
        },
        isVerificationStepFullFiled() {
            const verificationDocuments = this.request.verificationDocuments;
            if (verificationDocuments.length === 0) {
                return true;
            }

            let states = [];
            if (verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                states.push(!(this.evidenceReports.length === 0));
                //  by default we add one empty document
                if (this.evidenceReports.length === 1 && !this.evidenceReports[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                states.push(!(this.compliantStatement.length === 0));
                //  by default we add one empty document
                if (this.compliantStatement.length === 1 && !this.compliantStatement[0].id) {
                    states.push(false);
                }
            }

            const state = states.includes(false) === false;
            this.changeStepValidity(StepId.VERIFICATION, state);
            return state;
        },
        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.evidenceReports.length && this.evidenceReports[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.compliantStatement.length && this.compliantStatement[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
    },
    watch: {
        isBulkGeneralProductInformationValid(isValid) {
            if (this.isExternalBulkReplyMode) {
                this.changeStepValidity(StepId.GENERAL_PRODUCT_INFORMATION, isValid);
            }
        },
        evidenceReports() {
            this.managePredefinedDocuments();
        },
        isVerificationStepRendered(isRendered) {
            if (isRendered) {
                this.$nextTick(() => this.managePredefinedDocuments());
            }
        }
    }
}
</script>
<style lang="scss">
#simple-flow {
    color: black;
    .card-title, .reply-process-heading, .table, h6, label, h2, .simple-flow-instruction {
        color: black;
    }
    .verification-component .card-header {
        h4 {
            font-size: 1rem;
        }
        padding: 0.5rem 0 0.5rem 1rem;
    }
}
.vertical-steps-flow > .card {
    border: 1px solid #d5d3da !important;
}
.vertical-steps-flow > .card.done {
    border-color: #21b764b3 !important;

    background-position: right 3rem top 1.23rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2321b764b3' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: calc(0.725em + 0.438rem) calc(0.725em + 0.438rem);
}
.collapse-margin .card.border-success {
    border-bottom: 1px solid #21b764b3 !important;
}
.collapse-margin .card.border-warning {
    border-bottom: 1px solid #ffdb3c !important;
}
.vertical-steps-flow .card {
    .component-concern-elements > .card {
        border: 1px solid #d5d3da !important;
    }

    .card.component-concern-elements.has-concern-elements:not(.collapsed) > .card-title {
        border-bottom: 1px solid #d5d3da;
    }

    .card.component-concern-elements.has-concern-elements.collapsed > .card-title {
        border-bottom: none;
        margin-bottom: 0;
    }

    .card.component-concern-elements.no-concern-elements > .card-title {
        margin-bottom: 0;
    }

    .card:not(.border-success):not(.border-warning) {
        border: 1px solid rgba(34, 41, 47, 0.125) !important;
    }

    .document-row:last-child {
        margin-bottom: 2REM;
    }
}
</style>
